import React from 'react';

import {Route} from 'mobx-router';
import {RootStore} from './root-store';
import {ChatBase, ChatPage} from '../chat/ChatPage';
import {Questions} from '../questions/Questions';
import {CompanyPage} from '../company/CompanyPage';
import {PrivacyPolicyPage} from '../privacypolicy/PrivacyPolicyPage';
import {WebappHeader} from './WebappHeader';
import {ErrorBoundary} from './ErrorBoundary';
import {EmailRaport, TeamDashboardPage} from '../dashboard/TeamDashboardPage';
import {MyDashboardPage} from '../dashboard/MyDashboardPage';
import {RequireLogin} from './RequireLogin';
import {AdminLoginPage} from '../user/AdminLoginPage';
import {RequireWeakLogin} from './RequireWeakLogin';
import {Capacitor} from '@capacitor/core';
import {OnboardingDashboard} from '../dashboard/OnboardingDashboard';
import {StationPage} from '../station/StationPage';
import {CompanySignUpPage} from '../signup/CompanySignUpPage';
import {FeedbackPage} from '../feedback/FeedbackPage';
import {SlackInstallPage} from '../company/SlackInstallPage';
import {Language} from './label-store';
import {MentorPage} from '../mentor/MentorPage';
import {CompanyDeletedConfirmation} from '../dashboard/CompanyDeletedConfirmation';

export const routes = {
    fromEmailToDashboard: new Route<RootStore, { employeeId: string; }, { department?: string; segment?: string; token?: string; tenantId?: string }>({
        path: '/results/:employeeId',
        component: <><WebappHeader/><ErrorBoundary><EmailRaport/></ErrorBoundary></>,
        onEnter: (_, params, store, queryParams): void => store.teamResultsStore.onTokenLogin(params.employeeId, queryParams.department, queryParams.token, queryParams.tenantId),
    }),
    slackAuthentication: new Route<RootStore, { _ : never }, { code?: string; error?: string; state?: string }>({
        path: '/slack/oauth/completion',
        component: <><SlackInstallPage/></>,
        onEnter: (_, _params, store, queryParams): void => store.companyStore.onSlackAuthentication(queryParams.code, queryParams.error, queryParams.state),
    }),
    chatHistory: new Route<RootStore, { employeeId: string; }, { token?: string; tenantId?: string }>({
        path: '/chat/history/:employeeId',
        component: <><WebappHeader/><ChatBase><ChatPage/></ChatBase></>,
        onEnter: (_, params, store, queryParams): void => store.chatViewStore.onLogin(params.employeeId, queryParams.token, queryParams.tenantId),
    }),
    chatAskQuestion: new Route<RootStore, { questionId: string; }, { token?: string; tenantId?: string }>({
        path: '/chat/ask/:questionId',
        component: <><WebappHeader/><ChatBase><ChatPage/></ChatBase></>,
        onEnter: (_, params, store, queryParams): void => store.chatViewStore.onLoginAndAskQuestion(params.questionId, queryParams.token, queryParams.tenantId),
    }),
    chatAnswerQuestion: new Route<RootStore, { questionId: string; answer: '5' | '4' | '3' | '2' | '1' | 'text'; }, { token?: string, tenantId?: string }>({
        path: '/chat/answer/:questionId/:answer',
        component: <><WebappHeader/><ChatBase><ChatPage/></ChatBase></>,
        onEnter: (_, params, store, queryParams): void => store.chatViewStore.onLoginAndAnswerQuestion(params.questionId, params.answer, queryParams.token, queryParams.tenantId),
    }),
    chatAnswerQuestionDepricated: new Route<RootStore, { questionId: string; answer: '5' | '4' | '3' | '2' | '1' ; }, { token?: string; tenantId?: string }>({
        path: '/chat/multiple-answer/:questionId/:answer',
        component: <><WebappHeader/><ChatBase><ChatPage/></ChatBase></>,
        onEnter: (_, params, store, queryParams): void => store.chatViewStore.onLoginAndAnswerQuestion(params.questionId, params.answer, queryParams.token, queryParams.tenantId),
    }),
    chatChooseLanguage: new Route<RootStore, { employeeId: string; language: Language; }, { token?: string; tenantId?: string }>({
        path: '/chat/onboarding/:employeeId/:language',
        component: <><WebappHeader/><ChatBase><ChatPage/></ChatBase></>,
        onEnter: (_, params, store, queryParams): void => store.chatViewStore.onLoginOnboardingAndChooseLanguage(params.employeeId, params.language, queryParams.token, queryParams.tenantId),
    }),
    chatAnswerWeeklyQuestions: new Route<RootStore, { employeeId: string }, { token?: string; tenantId?: string }>({
        path: '/chat/weekly-questions/:employeeId',
        component: <><WebappHeader/><ChatBase><ChatPage/></ChatBase></>,
        onEnter: (_, params, store, queryParams): void => store.chatViewStore.onLoginWeeklyQuestions(params.employeeId, queryParams.token, queryParams.tenantId),
    }),
    mentorAskAdvice: new Route<RootStore, { employeeId: string }, { questionText?: string; catalogQuestionId?: string; questionScore?: string; filterCategory?: string, filterPeriodId?: string, filterBusinessUnitId?: string, filterLocation?: string, filterEmployeeSegment?: string, filterRole?: string, filterTag?: string, filterServiceLength?: string, filterQuestionId?: string, displayQuestion: string, summaryType?: string}>({
        path: '/mentor/advice/:employeeId',
        component: <><RequireWeakLogin><WebappHeader/><ChatBase><MentorPage/></ChatBase></RequireWeakLogin></>,
        onEnter: (_, params, store, queryParams): void => store.mentorStore.onEnterAndLoadInitialAdvice(params.employeeId, queryParams.catalogQuestionId, queryParams.questionText, queryParams.questionScore, queryParams.filterPeriodId, queryParams.filterBusinessUnitId, queryParams.filterEmployeeSegment, queryParams.filterLocation, queryParams.filterRole, queryParams.filterTag, queryParams.filterServiceLength, queryParams.filterCategory, queryParams.filterQuestionId, queryParams.displayQuestion, queryParams.summaryType),
    }),
    mentorHistory: new Route<RootStore>({
        path: '/mentor',
        component: <RequireWeakLogin><WebappHeader/><ChatBase><MentorPage
            loadContext="history"
            isNotFromQuestion={true}
        /></ChatBase></RequireWeakLogin>,
    }),
    chat: new Route<RootStore>({
        path: '/chat',
        component: <RequireWeakLogin><WebappHeader/><ChatBase><ChatPage
            loadContext="history"/></ChatBase></RequireWeakLogin>,
    }),
    chatStation: new Route<RootStore, { businessUnitId: string; }>({
        path: '/station/:businessUnitId',
        component: <StationPage/>,
        onEnter: (_, params, store): void => store.stationStore.onEnter(params.businessUnitId),
        onExit: (_, _params, store): void => store.stationStore.onExit(),
    }),
    home: new Route<RootStore>({
        path: '/',
        component: Capacitor.getPlatform() === 'web' ?
            (<RequireWeakLogin><WebappHeader/><ErrorBoundary><TeamDashboardPage /></ErrorBoundary></RequireWeakLogin>) :
            (<RequireWeakLogin><WebappHeader/><ChatBase><ChatPage
                loadContext="history"/></ChatBase></RequireWeakLogin>),
    }),
    dashboard: new Route<RootStore>({
        path: '/results',
        component: (
            <RequireWeakLogin>
                <WebappHeader/>
                <ErrorBoundary>
                    <TeamDashboardPage/>
                </ErrorBoundary>
            </RequireWeakLogin>
        ),
    }),
    companyDeleted: new Route<RootStore, { employeeId: string; }, {token?: string; tenantId?: string }>({
        path: '/company-deleted/:employeeId',
        component: (
            <CompanyDeletedConfirmation/>
        ),
        onEnter: (_, params, store, queryParams): void => store.teamResultsStore.onTokenLogin(params.employeeId, queryParams.token, queryParams.tenantId),
    }),

    myDashboard: new Route<RootStore>({
        path: '/my-result',
        component: (
            <RequireWeakLogin>
                <WebappHeader/>
                <ErrorBoundary>
                    <MyDashboardPage/>
                </ErrorBoundary>
            </RequireWeakLogin>
        ),
    }),
    onboarding: new Route<RootStore>({
        path: '/onboarding',
        component: (
            <RequireWeakLogin>
                <WebappHeader/>
                <ErrorBoundary>
                    <OnboardingDashboard/>
                </ErrorBoundary>
            </RequireWeakLogin>
        ),
    }),
    questions: new Route<RootStore>({
        path: '/questions',
        component: (
            <RequireLogin>
                <WebappHeader/>
                <ErrorBoundary>
                    <Questions/>
                </ErrorBoundary>
            </RequireLogin>
        ),
    }),
    company: new Route<RootStore>({
        path: '/company',
        component: (
            <RequireLogin>
                <WebappHeader/>
                <ErrorBoundary>
                    <CompanyPage/>
                </ErrorBoundary>
            </RequireLogin>
        ),
    }),
    feedback: new Route<RootStore>({
        path: '/feedback',
        component: (
            <RequireLogin requireAdmin={true} disableRedirectToDashboard>
                <WebappHeader/>
                <ErrorBoundary>
                    <FeedbackPage/>
                </ErrorBoundary>
            </RequireLogin>
        ),
    }),
    companySignUp: new Route<RootStore>({
        path: '/signup',
        component: (
            <CompanySignUpPage/>
        ),
    }),
    privacypolicy: new Route<RootStore>({
        path: '/privacypolicy',
        component: (
            <ErrorBoundary>
                <PrivacyPolicyPage/>
            </ErrorBoundary>
        ),
    }),
    admin: new Route<RootStore>({
        path: '/super-user-gateway-1318d09c-64c1-42a4-aadf-3827536a4467',
        component: (
            <AdminLoginPage redirectToDashboard/>
        ),
    }),
};
